import { Component, inject, OnInit } from '@angular/core';
import { FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CompoApp } from '../../../../services/CompoApp';
import { UserUpdateView } from '../../../../models/user/input/UserUpdateView';
import { ActivatedRoute, Router } from '@angular/router';
import Constants from '../../../../Constants';
import { User } from '../../../../models/user/User';
import ErrorCode from '../../../../enums/ErrorCode';
import { UserRequestService } from '../../../../services/user-request.service';
import { MessageDesignBUtils } from '../../../components/message-design-b/message-design-b.utils';
import {
  GlobalMessageClientService
} from '../../../../services/client/global-message-client-service/global-message-client.service';
import { UnsubscribeBaseModel } from '../../../../base/model/UnsubscribeBase.model';
import { PropagateOpts } from '../../../../models/form/base-cp-form.model';
import { takeUntil } from 'rxjs/operators';
import { AuthPageCommonComponent } from '../auth-page-common.component';
import { ClickOutsideDirective } from '../../../../directives/click-outside/click-outside.directive';
import { FormInputComponent } from '../../../components/input/form-input/form-input.component';
import { PasswordRequirementsComponent } from '../../../components/password-requirements/password-requirements.component';
import { ButtonComponent } from '../../../globals/button/button/button.component';
import { MessageDesignBSmallComponent } from '../../../components/message-design-b/message-design-b-small/message-design-b-small.component';
import { TranslateModule } from '@ngx-translate/core';

/**
 * ResetPasswordPageComponent used for following user case:
 * 1. Admin create a new user without email with a default password
 * After the first login the user must change the previous password
 * 2. Admin clicked in the user detail view the **Change Password** and give a new default password for the user
 * After the first login the user must change the previous password
 */
@Component({
    selector: 'cp-reset-password-page-component',
    templateUrl: './reset-password-page.component.html',
    styleUrls: ['./reset-password-page.component.scss', '../auth-page-common.scss'],
    imports: [AuthPageCommonComponent, ClickOutsideDirective, FormInputComponent, PasswordRequirementsComponent, ButtonComponent, MessageDesignBSmallComponent, TranslateModule]
})
export class ResetPasswordPageComponent extends UnsubscribeBaseModel implements OnInit {

  //region READONLY FIELDS
  readonly PASSWORD_MIN_LENGTH: number = 8;
  //endregion

  //region INJECTIONS
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private app: CompoApp = inject(CompoApp);
  private userRequestService: UserRequestService = inject(UserRequestService);
  private messageClientService: GlobalMessageClientService = inject(GlobalMessageClientService);
  //endregion

  //region FORM
  readonly PASSWORD: string = 'password';
  readonly formGroup: UntypedFormGroup = new UntypedFormGroup({
    [this.PASSWORD]: new FormControl(null)
  });
  //endregion

  //region GETTERS
  get isValid(): boolean {
    return this.formGroup?.valid;
  }

  get resetFailedText(): string {
    if (!this.showResetFailedError) {
      return null;
    }

    switch (this.resetFailedReason) {
      case ErrorCode.USER_INACTIVE_OR_NO_ACTIVE_MAPPINGS: {
        return 'MESSAGE_TEXTS.LOGIN.USER_INACTIVE_OR_NO_ACTIVE_MAPPINGS';
      }
      case ErrorCode.CAN_NOT_RESET_PASSWORD_TO_EXISTING_ONE: {
        return 'MESSAGE_TEXTS.RESET_PASSWORD.NEW_PASSWORD_CAN_NOT_BE_THE_OLD';
      }
      case ErrorCode.WRONG_USERNAME_OR_PASSWORD:
      default: {
        return 'MESSAGE_TEXTS.LOGIN.WRONG_USER_NAME_OR_PASSWORD';
      }
    }
  }

  //endregion

  //region FIELDS
  userId: string;
  isUserOrganizationWide: boolean;
  showResetFailedError: boolean = false;
  resetFailedReason: number;
  showPasswordInputErrors: boolean = false;
  passwordValidatorsSet: boolean = false;
  inProgress: boolean;
  user: User;
  //endregion

  //region LIFECYCLE
  ngOnInit(): void {
    this.route.params.subscribe(param => {
      const userId = param['userId'];
      if (userId) {
        this.getUser(userId);
      } else {
        this.router.navigate(['']);
        this.messageClientService.messages$.next(MessageDesignBUtils.wrongOrInvalideLinkForPassReset());
      }
    });

    this.formGroup.valueChanges
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((value: any) => {
        if (value) {
          const passwordValue: string = value[this.PASSWORD];
          if (this.showPasswordInputErrors && (!passwordValue || passwordValue.length === 0)) {
            this.showPasswordInputErrors = false;
          }
          if (!this.passwordValidatorsSet && passwordValue && passwordValue.length > 0) {
            this.setPasswordValidators();
          }
        }
      });
  }

  //endregion

  //region METHODS
  onPasswordInputLostFocus(): void {
    const password: string = this.formGroup.get(this.PASSWORD).value;
    if (password && password.length > 0 && !this.showPasswordInputErrors) {
      this.showPasswordInputErrors = true;
    }
  }

  onSaveNewPassword(): void {
    if (this.formGroup?.invalid || this.inProgress) {
      return;
    }

    const password: string = this.formGroup.get(this.PASSWORD).value;

    if (!password
      || !password.length
      || password.length < this.PASSWORD_MIN_LENGTH
      || !password.match(Constants.PASSWORD_REGEX)
    ) {
      this.setPasswordValidators();
      return;
    }

    this.inProgress = true;
    this.formGroup?.get(this.PASSWORD)?.disable(PropagateOpts.EMIT_EVENT_FALSE);

    this.app.userService.saveEditedUser(this.createUpdateView())
      .then((user: User) => {
        this.userId = user.id;
        setTimeout(() => {
          if (user.roleCount > 1) {
            this.router.navigate(['/selectRole'], { queryParams: { roleCount: user.roleCount } });
          } else {
            this.router.navigate(['/home/prozesse/jobs']);
            this.app.on.loginTrigger.next(this.app.data.getUser());
          }
        }, 1000);
      })
      .catch((error) => {
        if (error.error && error.error.errorCode && error.error.errorCode === ErrorCode.CAN_NOT_RESET_PASSWORD_TO_EXISTING_ONE) {
          this.showResetFailedError = true;
          this.resetFailedReason = ErrorCode.CAN_NOT_RESET_PASSWORD_TO_EXISTING_ONE;
          this.formGroup.reset();
        } else if (error.error && error.error.code && error.error.code === ErrorCode.USER_INACTIVE_OR_NO_ACTIVE_MAPPINGS) {
          this.showResetFailedError = true;
          this.resetFailedReason = ErrorCode.USER_INACTIVE_OR_NO_ACTIVE_MAPPINGS;
          this.formGroup.reset();
        } else if (error.error && error.error.code && error.error.code === ErrorCode.WRONG_USERNAME_OR_PASSWORD) {
          this.showResetFailedError = true;
          this.resetFailedReason = ErrorCode.WRONG_USERNAME_OR_PASSWORD;
        } else {
          this.router.navigate(['']);
          this.messageClientService.messages$.next(MessageDesignBUtils.passwordResetNotPossible());
        }
        this.inProgress = false;
        this.formGroup?.get(this.PASSWORD)?.enable(PropagateOpts.EMIT_EVENT_FALSE);
      });
  }

  private getUser(userId: string) {
    this.userRequestService.getUserById(userId)
      .then((user: User) => {
        this.user = user;
        this.userId = user.id;
        if (!user.isResetPasswordOnLogin) {
          this.router.navigate(['']);
          this.messageClientService.messages$.next(MessageDesignBUtils.wrongOrInvalideLinkForPassReset());
        }
        this.formGroup.reset({}, PropagateOpts.EMIT_EVENT_FALSE);
      })
      .catch(() => {
        this.router.navigate(['']);
        this.messageClientService.messages$.next(MessageDesignBUtils.wrongOrInvalideLinkForPassReset());
      });
  }

  private createUpdateView(): UserUpdateView {
    const password = this.formGroup.get(this.PASSWORD).value;
    const updateview = new UserUpdateView(
      this.userId,
      null,
      password,
      null,
      null,
      null,
      null,
      null,
      null,
      this.isUserOrganizationWide,
      null,
      null
    );
    updateview.setisResetPasswordOnLogin(false);
    return updateview;
  }

  private setPasswordValidators(): void {
    const passwordControl: FormControl = this.formGroup.get(this.PASSWORD) as FormControl;
    if (passwordControl) {
      passwordControl.setValidators(
        [
          Validators.required, Validators.minLength(8),
          Validators.pattern(Constants.PASSWORD_REGEX)
        ]
      );
      passwordControl.updateValueAndValidity(PropagateOpts.EMIT_EVENT_FALSE);
      this.passwordValidatorsSet = true;
    }
  }

  //endregion
}
